import React from "react";
import { Ticket, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatINR } from "@/lib/api";
import { fmtGrams } from "@/pages/admin/orders/orderConstants";

// Dual-currency payment capture dialog: INR (bank/UPI/cash/cheque), Metal (gold weight +
// purity), an active partner Credit Note redeemed as a voucher (Reference dropdown), or a
// combined "Settle Gold Balance in Rupees" action (converts the gold debt to cash at today's rate).
export const RecordPaymentDialog = ({
  payFor, onClose, payKind, setPayKind, payAmount, setPayAmount, payDate, setPayDate,
  payMode, setPayMode, payRef, setPayRef, payCheque, setPayCheque, payNote, setPayNote,
  payGoldWeight, setPayGoldWeight, payGoldPurity, setPayGoldPurity, payGoldFinePct, setPayGoldFinePct,
  goldFinePreview, staff, busy, onSubmit,
  creditNotes = [], selectedCreditNoteId, onSelectCreditNote,
  goldRate, settleGoldInRupee, onToggleSettleGold,
}) => {
  const selectedCn = creditNotes.find((c) => c.id === selectedCreditNoteId);
  const goldOutstanding = Number(payFor?.gold_outstanding_fine_g || 0);
  const maxSettleRupees = goldRate ? Math.round(goldOutstanding * goldRate * 100) / 100 : 0;
  const gramsToSettle = goldRate && Number(payAmount) > 0 ? Math.min(Number(payAmount) / goldRate, goldOutstanding) : 0;
  return (
  <Dialog open={!!payFor} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="order-payment-dialog">
      <DialogHeader><DialogTitle>Record Payment — {payFor?.invoice_no || payFor?.order_no}</DialogTitle></DialogHeader>
      {payFor && (
        <div className="grid grid-cols-2 gap-2 -mt-2 text-sm">
          <div className="rounded-lg bg-[#F7F0E6] p-2.5">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Rupee Outstanding</div>
            <div data-testid="pay-inr-outstanding" className="font-mono font-medium text-[#7A1F1F]">{formatINR(payFor.outstanding_amount)}</div>
          </div>
          <div className="rounded-lg bg-[hsl(var(--gold-light))] p-2.5">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Gold Outstanding</div>
            <div data-testid="pay-gold-outstanding" className="font-mono font-medium text-[hsl(var(--gold-dark))]">{fmtGrams(payFor.gold_outstanding_fine_g)} fine</div>
          </div>
        </div>
      )}
      <Tabs value={payKind} onValueChange={setPayKind} className="mt-1">
        <TabsList className="grid grid-cols-2 w-full">
          <TabsTrigger data-testid="pay-mode-inr" value="inr">INR (Rupee)</TabsTrigger>
          <TabsTrigger data-testid="pay-mode-metal" value="metal">Metal (Gold)</TabsTrigger>
        </TabsList>

        <TabsContent value="inr" className="mt-3">
          <div className="grid grid-cols-2 gap-3">
            {goldOutstanding > 0.001 && !selectedCn && (
              <div className="col-span-2 rounded-lg border border-border bg-[hsl(var(--gold-light))]/50 p-2.5 flex items-center justify-between gap-3">
                <div className="pr-2">
                  <Label className="text-xs flex items-center gap-1.5"><Coins className="h-3.5 w-3.5 text-[hsl(var(--gold-dark))]" /> Settle Gold Balance in Rupees</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Converts the {fmtGrams(goldOutstanding)} gold balance to cash at today's rate{goldRate ? ` (${formatINR(goldRate)}/g)` : ""}.
                  </p>
                </div>
                <Switch data-testid="settle-gold-in-rupee-switch" checked={settleGoldInRupee} onCheckedChange={onToggleSettleGold} disabled={!goldRate} />
              </div>
            )}
            {!settleGoldInRupee && (
              <div className="col-span-2 rounded-lg border border-border bg-[#FBF6EE] p-2.5">
                <Label className="text-xs flex items-center gap-1.5"><Ticket className="h-3.5 w-3.5 text-[hsl(var(--primary))]" /> Reference — Credit Note (voucher)</Label>
                <Select value={selectedCreditNoteId || "none"} onValueChange={onSelectCreditNote}>
                  <SelectTrigger data-testid="pay-credit-note-select" className="mt-1.5 bg-background"><SelectValue placeholder="No credit note — manual reference" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No credit note — manual reference</SelectItem>
                    {creditNotes.map((cn) => (
                      <SelectItem key={cn.id} value={cn.id}>{cn.credit_note_no} — {formatINR(cn.remaining_amount)} available</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedCn ? (
                  <p data-testid="pay-credit-note-hint" className="text-xs text-muted-foreground mt-1.5">
                    Voucher applied — amount auto-filled from the note's balance and capped at {formatINR(selectedCn.remaining_amount)}.
                  </p>
                ) : creditNotes.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-1.5">This partner has no active credit notes available to redeem.</p>
                ) : null}
              </div>
            )}
            <div className="col-span-2">
              <Label>Amount {settleGoldInRupee ? "to Settle Gold" : selectedCn ? "Redeemed" : "Received"} (₹)</Label>
              <Input data-testid="order-payment-amount-input" type="number" value={payAmount}
                onChange={(e) => {
                  let v = e.target.value;
                  const cap = settleGoldInRupee ? maxSettleRupees : (selectedCn ? selectedCn.remaining_amount : null);
                  if (cap != null && Number(v) > cap) v = String(cap);
                  setPayAmount(v);
                }}
                max={settleGoldInRupee ? maxSettleRupees : (selectedCn ? selectedCn.remaining_amount : undefined)}
                className="mt-1 font-mono" placeholder="0" />
              {settleGoldInRupee && (
                <p data-testid="settle-gold-preview" className="text-xs text-muted-foreground mt-1.5">
                  Settles <span className="font-mono font-medium text-[hsl(var(--gold-dark))]">{fmtGrams(gramsToSettle)}</span> of gold, capped at {formatINR(maxSettleRupees)}.
                </p>
              )}
            </div>
            <div><Label>Payment Date</Label><Input data-testid="order-payment-date-input" type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="mt-1" /></div>
            {!selectedCn && !settleGoldInRupee && (
              <div><Label>Mode</Label>
                <Select value={payMode} onValueChange={setPayMode}>
                  <SelectTrigger data-testid="order-payment-mode-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            {!selectedCn && !settleGoldInRupee && (
              <div><Label>UTR / Transaction Ref</Label><Input data-testid="order-payment-reference-input" value={payRef} onChange={(e) => setPayRef(e.target.value)} className="mt-1" placeholder="UTR / Txn ID" /></div>
            )}
            {!selectedCn && !settleGoldInRupee && payMode === "cheque" && (
              <div><Label>Cheque Number</Label><Input data-testid="order-payment-cheque-input" value={payCheque} onChange={(e) => setPayCheque(e.target.value)} className="mt-1 font-mono" /></div>
            )}
            <div className="col-span-2"><Label>Note (optional)</Label><Textarea value={payNote} onChange={(e) => setPayNote(e.target.value)} rows={2} /></div>
          </div>
        </TabsContent>

        <TabsContent value="metal" className="mt-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Gold Weight (g)</Label><Input data-testid="pay-gold-weight-input" type="number" value={payGoldWeight} onChange={(e) => setPayGoldWeight(e.target.value)} className="mt-1 font-mono" placeholder="0.000" /></div>
            <div><Label>Purity</Label>
              <Select value={payGoldPurity} onValueChange={setPayGoldPurity}>
                <SelectTrigger data-testid="pay-gold-purity-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="24K">24K (999)</SelectItem>
                  <SelectItem value="22K">22K (916)</SelectItem>
                  <SelectItem value="18K">18K (750)</SelectItem>
                  <SelectItem value="14K">14K (585)</SelectItem>
                  <SelectItem value="9K">9K (375)</SelectItem>
                  <SelectItem value="Other">Other (per-mille)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {payGoldPurity === "Other" && (
              <div><Label>Purity (per-mille)</Label><Input data-testid="pay-gold-finepct-input" type="number" value={payGoldFinePct} onChange={(e) => setPayGoldFinePct(e.target.value)} className="mt-1 font-mono" placeholder="e.g. 916" /></div>
            )}
            <div><Label>Payment Date</Label><Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="mt-1" /></div>
            <div className="col-span-2"><Label>Reference (optional)</Label><Input value={payRef} onChange={(e) => setPayRef(e.target.value)} className="mt-1" placeholder="Voucher / RTGS ref" /></div>
            <div className="col-span-2"><Label>Note (optional)</Label><Textarea value={payNote} onChange={(e) => setPayNote(e.target.value)} rows={2} /></div>
            <div className="col-span-2 rounded-lg bg-[hsl(var(--gold-light))] p-3 text-sm">
              Credits fine gold: <span data-testid="pay-gold-fine-preview" className="font-mono font-semibold text-[hsl(var(--gold-dark))]">{fmtGrams(goldFinePreview)}</span>
              <span className="text-muted-foreground"> (24K equivalent)</span>
            </div>
          </div>
        </TabsContent>
      </Tabs>
      <div className="text-xs text-muted-foreground">Received by: {staff?.name || "—"}</div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button data-testid="order-payment-submit"
          disabled={busy || (payKind === "inr" ? !payAmount : (!payGoldWeight || (payGoldPurity === "Other" && !payGoldFinePct)))}
          onClick={onSubmit}>Record {payKind === "metal" ? "Gold" : settleGoldInRupee ? "Gold Settlement" : selectedCn ? "Voucher" : "Payment"}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  );
};
